/**
 * 프로필링크 다국어 — 언어 코드 정본.
 *
 * 글로릴스(flowing-ai-avatar)의 `src/lib/langs.ts` HOME_LANG_OPTIONS 와 **코드가 일치해야 한다**.
 * 글로릴스가 인스타 슬롯을 만들 때 그 목록으로 검증하므로, 여기 없는 코드는 애초에 넘어오지 않는다.
 * 글로릴스에 언어가 추가되면 이 파일에도 같이 추가한다.
 */

export interface ProfileLinkLocale {
    /** 언어 코드 — 글로릴스 SocialSlot.lang 과 동일 */
    code: string;
    /** 짧은 표기 배지 (KO·US·JP…) — 국가 전환 칩에 쓴다 */
    badge: string;
    /** 국기 이모지 */
    flag: string;
    /** 국가명 — 발행 계정은 나라 단위라 언어명이 아니라 국가명으로 표기한다(글로릴스 규칙 동일) */
    country: string;
    /** 언어명 */
    label: string;
}

export const PROFILE_LINK_LOCALES: ProfileLinkLocale[] = [
    { code: 'ko', badge: 'KO', flag: '🇰🇷', country: '한국', label: '한국어' },
    { code: 'en', badge: 'US', flag: '🇺🇸', country: '미국', label: '영어' },
    { code: 'ja', badge: 'JP', flag: '🇯🇵', country: '일본', label: '일본어' },
    { code: 'zh', badge: 'CN', flag: '🇨🇳', country: '중국', label: '중국어' },
    { code: 'vi', badge: 'VN', flag: '🇻🇳', country: '베트남', label: '베트남어' },
    { code: 'id', badge: 'ID', flag: '🇮🇩', country: '인도네시아', label: '인도네시아어' },
    { code: 'es', badge: 'ES', flag: '🇪🇸', country: '스페인', label: '스페인어' },
    { code: 'th', badge: 'TH', flag: '🇹🇭', country: '태국', label: '태국어' },
    { code: 'ar', badge: 'SA', flag: '🇸🇦', country: '사우디아라비아', label: '아랍어' },
    { code: 'hi', badge: 'IN', flag: '🇮🇳', country: '인도', label: '힌디어' },
    { code: 'pt', badge: 'BR', flag: '🇧🇷', country: '브라질', label: '포르투갈어' },
    { code: 'fr', badge: 'FR', flag: '🇫🇷', country: '프랑스', label: '프랑스어' },
    { code: 'de', badge: 'DE', flag: '🇩🇪', country: '독일', label: '독일어' },
    { code: 'ru', badge: 'RU', flag: '🇷🇺', country: '러시아', label: '러시아어' },
    { code: 'tr', badge: 'TR', flag: '🇹🇷', country: '튀르키예', label: '터키어' },
    { code: 'it', badge: 'IT', flag: '🇮🇹', country: '이탈리아', label: '이탈리아어' },
    { code: 'nl', badge: 'NL', flag: '🇳🇱', country: '네덜란드', label: '네덜란드어' },
    { code: 'pl', badge: 'PL', flag: '🇵🇱', country: '폴란드', label: '폴란드어' },
    { code: 'ms', badge: 'MY', flag: '🇲🇾', country: '말레이시아', label: '말레이어' },
    { code: 'fil', badge: 'PH', flag: '🇵🇭', country: '필리핀', label: '필리핀어' },
    { code: 'uk', badge: 'UA', flag: '🇺🇦', country: '우크라이나', label: '우크라이나어' },
    { code: 'mn', badge: 'MN', flag: '🇲🇳', country: '몽골', label: '몽골어' },
    { code: 'my', badge: 'MM', flag: '🇲🇲', country: '미얀마', label: '미얀마어' },
];

/** 프로필링크 원본 언어 — 블록의 기존 필드(title 등)가 곧 이 언어의 내용이다 */
export const BASE_LOCALE_CODE = 'ko';

const LOCALE_MAP = new Map(PROFILE_LINK_LOCALES.map((l) => [l.code, l]));

/** 언어 코드 → 로케일 정보. 모르는 코드는 코드 자체를 국가명으로 폴백 */
export function getLocale(code: string): ProfileLinkLocale {
    return LOCALE_MAP.get(code) ?? { code, badge: code.toUpperCase().slice(0, 2), flag: '🏳️', country: code, label: code };
}

/** 언어 코드 → 국가명 ("미국") */
export function localeCountry(code: string): string {
    return getLocale(code).country;
}

/** 언어 코드 → 국기 + 국가명 ("🇺🇸 미국") */
export function localeFlagCountry(code: string): string {
    const l = getLocale(code);
    return `${l.flag} ${l.country}`;
}

/** PROFILE_LINK_LOCALES 정의 순서로 정렬 — 국가 버튼 노출 순서를 항상 같게 유지한다 */
export function sortByLocaleOrder<T>(items: T[], getCode: (item: T) => string): T[] {
    const order = new Map(PROFILE_LINK_LOCALES.map((l, i) => [l.code, i]));
    return [...items].sort(
        (a, b) => (order.get(getCode(a)) ?? 999) - (order.get(getCode(b)) ?? 999),
    );
}
